import React, { useEffect, useState } from 'react'
import { useMedications } from '../../hooks/useMedications'
import { useI18n } from '../../hooks/useI18n'
import StocksSection from '../stocks/StocksSection'
import TodayHistorySection from '../history/TodayHistorySection'
import StockUpdateModal from '../medications/StockUpdateModal'
import { useToast } from '../../contexts/ToastContext'

const HomePage: React.FC = () => {
  const {
    todayMedications,
    todayActivities,
    loading,
    progress,
    loadMedications,
    takeMedication,
    takeMedicationWithStockUpdate,
    checkStockBeforeTaking
  } = useMedications()
  const { t } = useI18n()
  const [takingMedication, setTakingMedication] = useState<string | null>(null)
  const [medicationsExpanded, setMedicationsExpanded] = useState(true)
  const [showStockModal, setShowStockModal] = useState(false)
  const [stockModalData, setStockModalData] = useState<{
    medicationId: string
    medicationName: string
    currentStock: number
    scheduledTime?: string
  } | null>(null)
  const { error: showError } = useToast()

  useEffect(() => {
    loadMedications()
  }, [loadMedications])

  // Deduplicate medications by ID to prevent duplicate rendering
  const uniqueMedications = React.useMemo(() => {
    const seen = new Set()
    const unique = todayMedications.filter(med => {
      if (seen.has(med.id)) {
        return false
      }
      seen.add(med.id)
      return true
    })
    return unique
  }, [todayMedications])

  // Derive taken status from todayActivities (single source of truth)
  const takenStatus = React.useMemo(() => {
    const status: Record<string, boolean> = {}
    
    // Process all taken activities
    todayActivities.forEach(activity => {
      if (activity.type === 'medication_taken' || activity.status === 'taken') {
        const scheduledTime = activity.scheduledTime || ''
        const key = scheduledTime 
          ? `${activity.medicationId}-${scheduledTime}`
          : `${activity.medicationId}-no-time`
        status[key] = true
      }
    })
    
    return status
  }, [todayActivities])

  const handleTakeMedication = async (medicationId: string, scheduledTime?: string) => {
    setTakingMedication(medicationId)
    try {
      // Check if we need to show stock modal
      const stockCheck = await checkStockBeforeTaking(medicationId, scheduledTime)

      if (stockCheck.shouldShowModal && stockCheck.medication) {
        // Show stock modal
        setStockModalData({
          medicationId,
          medicationName: stockCheck.medication.name,
          currentStock: stockCheck.medication.currentStock || stockCheck.medication.stock || 0,
          scheduledTime
        })
        setShowStockModal(true)
        setTakingMedication(null) // Reset loading state
      } else {
        // Take medication normally (optimistic update is handled in context)
        await takeMedication(medicationId, scheduledTime)
        setTakingMedication(null)
      }
    } catch (error) {
      console.error('Error taking medication:', error)
      showError(t('error'), t('error-taking-medication'))
      setTakingMedication(null)
    }
  }

  const handleStockUpdate = async (newStock: number) => {
    if (!stockModalData) return

    try {
      await takeMedicationWithStockUpdate(
        stockModalData.medicationId,
        stockModalData.scheduledTime,
        newStock
      )
      handleStockModalClose()
    } catch (error) {
      console.error('Error taking medication with stock update:', error)
      showError(t('error'), t('error-taking-medication'))
    }
  }

  const handleTakeWithoutUpdating = async () => {
    if (!stockModalData) return

    try {
      await takeMedication(stockModalData.medicationId, stockModalData.scheduledTime)
      handleStockModalClose()
    } catch (error) {
      console.error('Error taking medication without updating stock:', error)
      showError(t('error'), t('error-taking-medication'))
    }
  }

  const handleStockModalClose = () => {
    setShowStockModal(false)
    setStockModalData(null)
  }

  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':')
    const hour24 = parseInt(hours)
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
    const ampm = hour24 >= 12 ? 'PM' : 'AM'
    return `${hour12}:${minutes} ${ampm}`
  }

  const getProgressColor = (percentage: number): string => {
    if (percentage >= 80) return 'bg-green-500'
    if (percentage >= 50) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  const getMotivationalMessage = (): string => {
    if (!progress) return t('loading')

    const { percentage, takenMedications } = progress

    if (percentage === 100) {
      return t('progress-complete')
    } else if (percentage >= 80) {
      return t('progress-almost-complete')
    } else if (percentage >= 50) {
      return t('progress-good')
    } else if (takenMedications > 0) {
      return t('progress-started')
    } else {
      return t('progress-start')
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {t('home')}
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          {t('daily-summary')}
        </p>
      </div>

      {/* Stocks Section */}
      <StocksSection />

      {/* Today's Progress */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('today-progress')}
          </h2>
          <span className="text-2xl font-bold text-primary">
            {progress ? Math.round(progress.percentage) : 0}%
          </span>
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300 mb-2">
            <span>
              {progress ? progress.takenMedications : 0} {t('of')} {progress ? progress.totalMedications : 0} {t('medications-taken')}
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(progress?.percentage || 0)}`}
              style={{ width: `${progress?.percentage || 0}%` }}
            ></div>
          </div>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-300">
          {getMotivationalMessage()}
        </p>
      </div>



      {/* Today's Medications */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
        <button
          onClick={() => setMedicationsExpanded(!medicationsExpanded)}
          className="w-full flex items-center justify-between text-left focus:outline-none mb-4"
        >
          <div className="flex items-center">
            <svg className="w-6 h-6 text-primary mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('today-medications')}
            </h2>
            {todayMedications.length > 0 && !medicationsExpanded && (
              <span className="ml-2 bg-primary text-white text-xs px-2 py-1 rounded-full">
                {todayMedications.length}
              </span>
            )}
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 dark:text-gray-400 transform transition-transform ${medicationsExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {medicationsExpanded && (
          todayMedications.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path>
              </svg>
              <p>{t('no-medications')}</p>
            </div>
          ) : (
            <TodayMedicationsList
              medications={uniqueMedications}
              onTakeMedication={handleTakeMedication}
              takingMedication={takingMedication}
              formatTime={formatTime}
              takenStatus={takenStatus}
            />
          )
        )}
      </div>

      {/* Today's History */}
      <TodayHistorySection />

      {/* Stock Update Modal */}
      {showStockModal && stockModalData && (
        <StockUpdateModal
          isOpen={showStockModal}
          onClose={handleStockModalClose}
          onConfirm={handleStockUpdate}
          onTakeWithoutUpdating={handleTakeWithoutUpdating}
          medicationName={stockModalData.medicationName}
          currentStock={stockModalData.currentStock}
        />
      )}
    </div>
  )
}

// Subcomponent for today's medications list
interface MedicationEntry {
  medication: any
  time: string | null
}

interface TodayMedicationsListProps {
  medications: any[]
  onTakeMedication: (medicationId: string, scheduledTime?: string) => void
  takingMedication: string | null
  formatTime: (time: string) => string
  takenStatus: Record<string, boolean>
}

const TodayMedicationsList: React.FC<TodayMedicationsListProps> = ({
  medications, // These are now uniqueMedications passed from parent
  onTakeMedication,
  takingMedication,
  formatTime,
  takenStatus
}) => {
  const { t } = useI18n()

  // Removed local uniqueMedications and useEffect for checkTakenStatus as they are lifted to parent

  // Group medications by time, filtering out taken ones
  const groupedMedications = React.useMemo(() => {
    const groups: Record<string, MedicationEntry[]> = {}

    medications.forEach(medication => {
      if (!medication.times || medication.times.length === 0) {
        const statusKey = `${medication.id}-no-time`
        const isTaken = takenStatus[statusKey]

        // Only add if not taken
        if (!isTaken) {
          if (!groups['no-time']) groups['no-time'] = []
          groups['no-time'].push({ medication, time: null })
        }
      } else {
        medication.times.forEach((time: string) => {
          const statusKey = `${medication.id}-${time}`
          const isTaken = takenStatus[statusKey]

          // Only add if not taken
          if (!isTaken) {
            if (!groups[time]) groups[time] = []
            groups[time].push({ medication, time })
          }
        })
      }
    })

    return groups
  }, [medications, takenStatus])

  // Sort time groups
  const sortedTimeGroups = Object.entries(groupedMedications).sort(([a], [b]) => {
    if (a === 'no-time') return 1
    if (b === 'no-time') return -1
    return a.localeCompare(b)
  }) as [string, MedicationEntry[]][]

  return (
    <div className="space-y-6">
      {sortedTimeGroups.map(([timeKey, medicationEntries]) => {
        const timeDisplay = timeKey === 'no-time' ? t('no-specific-time') : formatTime(timeKey)

        return (
          <div key={`time-group-${timeKey}`} className="space-y-3">
            {/* Time Header */}
            <div className="flex items-center">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center mr-3">
                <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{timeDisplay}</h4>
              <span className="ml-2 px-2 py-1 bg-gray-200 dark:bg-gray-600 text-xs rounded-full text-gray-600 dark:text-gray-300">
                {medicationEntries.length} {medicationEntries.length > 1 ? t('medications') : t('medication')}
              </span>
            </div>

            {/* Medications for this time */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg divide-y divide-gray-200 dark:divide-gray-600">
              {medicationEntries.map(({ medication, time }: MedicationEntry, index) => {
                const uniqueKey = `${medication.id}-${time || 'no-time'}-${index}`
                const isLoading = takingMedication === medication.id

                return (
                  <div key={uniqueKey} className="p-4 flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <div className="w-10 h-10 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-center mr-4">
                        {medication.pillSvg ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: medication.pillSvg
                                .replace('width="60"', 'width="24"')
                                .replace('height="60"', 'height="24"')
                            }}
                          />
                        ) : (
                          <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path>
                          </svg>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 dark:text-white">{medication.name}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {medication.dosage}mg
                          {medication.instructions && ` • ${medication.instructions}`}
                        </p>
                        {medication.stock !== undefined && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t('stock')}: {medication.stock} {t('units')}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="ml-4">
                      <button
                        onClick={() => onTakeMedication(medication.id, time || undefined)}
                        disabled={isLoading}
                        className="bg-primary text-white px-4 py-2 rounded-md text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center"
                      >
                        {isLoading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                        )}
                        {t('take')}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default HomePage 